import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { MastraClient } from '@mastra/client-js';
import { catchError, from, map, Observable, throwError } from 'rxjs';

import {
  injectTellentAssistantActorGetter,
  injectTellentAssistantAuthTokenGetter,
  injectTellentAssistantConfig,
} from '@core/tellent-assistant-contracts';
import type { ThreadSummary } from '@core/tellent-assistant-types';

import { AGENT_ID, buildTellentAssistantHeaders } from './tellent-assistant-headers';

@Injectable({ providedIn: 'root' })
export class TellentAssistantMastraClientService {
  private readonly config = injectTellentAssistantConfig();
  private readonly getToken =
    injectTellentAssistantAuthTokenGetter({ optional: true }) ?? (() => null);
  private readonly getActor = injectTellentAssistantActorGetter({ optional: true }) ?? (() => null);

  public listThreads(resourceId: string): Observable<ThreadSummary[]> {
    const client = this.makeClient();
    return from(client.listMemoryThreads({ agentId: AGENT_ID, resourceId })).pipe(
      map(res =>
        res.threads.map(
          t =>
            <ThreadSummary>{
              id: t.id,
              title: t.title ?? '',
              updatedAt: new Date(t.updatedAt),
            },
        ),
      ),
      catchError((err: unknown) => this.toHttpError(err)),
    );
  }

  public loadMessages(threadId: string): Observable<unknown[]> {
    const client = this.makeClient();
    const thread = client.getMemoryThread({ threadId, agentId: AGENT_ID });
    return from(thread.listMessages()).pipe(
      map(res => (<{ messages: unknown[] }>res).messages),
      catchError((err: unknown) => this.toHttpError(err)),
    );
  }

  public deleteThread(threadId: string): Observable<void> {
    const client = this.makeClient();
    const thread = client.getMemoryThread({ threadId, agentId: AGENT_ID });
    return from(thread.delete()).pipe(
      map(() => void 0),
      catchError((err: unknown) => this.toHttpError(err)),
    );
  }

  private makeClient(): MastraClient {
    return new MastraClient({
      baseUrl: this.config.apiUrl,
      headers: buildTellentAssistantHeaders(
        this.getToken(),
        this.getActor(),
        this.config.devPlayground,
      ),
    });
  }

  private toHttpError(err: unknown): Observable<never> {
    const message = err instanceof Error ? err.message : 'Unknown Mastra client error';
    return throwError(
      () =>
        new HttpErrorResponse({
          error: err,
          status: 0,
          statusText: message,
          url: this.config.apiUrl,
        }),
    );
  }
}
